import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as FolderIcon } from 'assets/images/folder .svg'
import { isNotNil } from 'utils/ramda'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { propOr } from 'ramda'
import { useSelector } from 'react-redux'
import { getFileTypeName } from 'utils/bpData'
import File from 'features/bpDetails/Components/File'
import { sortFilesByExpirationStatus } from 'utils/files'
import { getFileTypes } from 'ducks/files/selectors'
import { selectUserRelationToBp } from 'features/bpDetails/ducks/selectors'
import AddMultipleFilesModal from 'features/bpDetails/Components/files/AddMultipleFilesModal'

import EditIcon from '@mui/icons-material/Edit'
import ShareIcon from '@mui/icons-material/Share'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import LockIcon from '@mui/icons-material/Lock'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import { IconButton, Tooltip } from '@mui/material'

import BulkShareFilesModal from 'features/bpDetails/Components/ShareBulkFilesModal'
import BulkEditFileModal from 'features/bpDetails/Components/EditBulkFilesModal'
import BulkUnshareFilesModal from 'features/bpDetails/Components/UnshareBulkFilesModal'

const UnitFolders = ({ unit }) => {
  const [currentFolder, setCurrentFolder] = useState(null)
  const hasSelectedFolder = isNotNil(currentFolder)
  const fileTypes = useSelector(getFileTypes)
  const userRoles = useSelector(selectUserRelationToBp)
  const isProspectivePurchaser =
    userRoles.includes('prospective_purchaser') && userRoles.length === 1
  const [selectedFiles, setSelectedFiles] = useState([])
  const [bulkShareModalOpen, setBulkShareModalOpen] = useState(false)
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false)
  const [bulkUnshareModalOpen, setBulkUnshareModalOpen] = useState(false)

  const handleSelectFile = (fileId, isChecked) => {
    setSelectedFiles(prev =>
      isChecked ? [...prev, fileId] : prev.filter(id => id !== fileId)
    )
  }

  const allFolders = isProspectivePurchaser
    ? [
      {
        id: 'sharedFiles',
        name: 'Files shared with unit',
        description: 'Files shared from the Building Passport',
        icon: FolderSharedIcon,
        files: propOr([], 'sharedFiles', unit)
      }
    ]
    : [
      {
        id: 'files',
        name: 'General',
        description: 'Visible to all unit users',
        icon: FolderOpenIcon,
        files: propOr([], 'files', unit)
      },
      {
        id: 'privateFiles',
        name: 'Private files',
        description: 'Only visible to you',
        icon: LockIcon,
        files: propOr([], 'privateFiles', unit)
      },
      {
        id: 'sharedFiles',
        name: 'Files shared with unit',
        description: 'Files shared from the Building Passport',
        icon: FolderSharedIcon,
        files: propOr([], 'sharedFiles', unit)
      }
    ]

  useEffect(() => {
    if (unit && isNotNil(currentFolder)) {
      setCurrentFolder(prev => ({
        ...prev,
        files: propOr([], prev.id, unit)
      }))
    }
  }, [unit])

  const handleCategoryChange = folderObj => {
    setCurrentFolder(folderObj)
    setSelectedFiles([])
  }

  return (
    <SectionWrapper withMargin>
      <HeaderWrapper>
        <HeaderTitleWrapper>
          <SectionHeader
            open={isNotNil(currentFolder)}
            onClick={() => setCurrentFolder(null)}
          >
            Folders
          </SectionHeader>
          {isNotNil(currentFolder) && (
            <>
              <BreadcrumbsSign>&gt;</BreadcrumbsSign>
              <CurrentFolderName>{currentFolder.name}</CurrentFolderName>
            </>
          )}
        </HeaderTitleWrapper>

        {!isProspectivePurchaser && (
          <AddButtonWrapper>
            <Tooltip title='Uploaded files will be visible to all unit users' arrow>
              <div>
                <AddMultipleFilesModal unit={unit}>
                  <StyledAddButton>
                    <FolderOpenIcon style={{ fontSize: 14 }} />
                    Add general files
                  </StyledAddButton>
                </AddMultipleFilesModal>
              </div>
            </Tooltip>
            <Tooltip title='Uploaded files will only be visible to you' arrow>
              <div>
                <AddMultipleFilesModal isPrivate unit={unit}>
                  <StyledAddButton>
                    <LockIcon style={{ fontSize: 14 }} />
                    Add private files
                  </StyledAddButton>
                </AddMultipleFilesModal>
              </div>
            </Tooltip>
          </AddButtonWrapper>
        )}
      </HeaderWrapper>
      <CategoriesWrapper $listMode={!!currentFolder}>
        {hasSelectedFolder && (
          <CurrentCategoryTitle>{currentFolder.name}</CurrentCategoryTitle>
        )}
        {!hasSelectedFolder &&
          allFolders.map(category => {
            const filesCount = propOr([], 'files', category).length
            const CategoryIcon = category.icon
            return (
              <Tooltip
                key={category.id}
                title={category.description}
                placement='top'
                arrow
              >
                <FolderWrapper
                  onClick={
                    filesCount > 0
                      ? () => handleCategoryChange(category)
                      : () => {}
                  }
                  disabled={filesCount === 0}
                >
                  <FolderIcon />
                  <FolderNameWrapper>
                    <FolderNameRow>
                      <CategoryIcon style={{ fontSize: 14, opacity: 0.6 }} />
                      <FolderName>{category.name}</FolderName>
                    </FolderNameRow>
                    <FilesCounter>({filesCount})</FilesCounter>
                  </FolderNameWrapper>
                </FolderWrapper>
              </Tooltip>
            )
          })}

        {currentFolder && (
          <>
            {selectedFiles.length > 0 && (
              <BulkActionsBar>
                <Tooltip title='Edit selected files'>
                  <IconButton onClick={() => setBulkEditModalOpen(true)}>
                    <EditIcon fontSize='small' />
                  </IconButton>
                </Tooltip>

                <Tooltip title='Share selected files'>
                  <IconButton onClick={() => setBulkShareModalOpen(true)}>
                    <ShareIcon fontSize='small' />
                  </IconButton>
                </Tooltip>

                <Tooltip title='Unshare selected files'>
                  <IconButton onClick={() => setBulkUnshareModalOpen(true)}>
                    <LinkOffIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </BulkActionsBar>
            )}

            <FilesTableScrollWrapper>
              <FilesContainer>
                <FilesHeaderRow>
                  <HeaderCell style={{ width: '32px' }} />
                  <HeaderName>Name</HeaderName>
                  <HeaderType>Type</HeaderType>
                  <HeaderDate>Uploaded</HeaderDate>
                  <HeaderUser>Uploader</HeaderUser>
                  <HeaderCell style={{ width: '60px' }} /> {/* actions */}
                </FilesHeaderRow>

                {sortFilesByExpirationStatus(
                  propOr([], 'files', currentFolder)
                ).map((file, idx) => (
                  <File
                    key={file.id}
                    file={file}
                    type={getFileTypeName(
                      file?.fileType?.id,
                      fileTypes
                    )}
                    isUnit
                    unit={unit}
                    isPrivate={currentFolder.id === 'privateFiles'}
                    isShared={currentFolder.id === 'sharedFiles'}
                    isSelected={selectedFiles.includes(file.id)}
                    onSelect={handleSelectFile}
                    rowIndex={idx}
                    viewMode='list'
                  />
                ))}
              </FilesContainer>
            </FilesTableScrollWrapper>
          </>
        )}
      </CategoriesWrapper>

      <BulkShareFilesModal
        open={bulkShareModalOpen}
        onClose={() => setBulkShareModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />
      <BulkEditFileModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />
      <BulkUnshareFilesModal
        open={bulkUnshareModalOpen}
        onClose={() => setBulkUnshareModalOpen(false)}
        fileIds={selectedFiles || []}
        unit={unit}
      />
    </SectionWrapper>
  )
}

export default UnitFolders

const SectionWrapper = styled.div`
  margin-top: ${({ withMargin }) => (withMargin ? '30px' : '0')};
`

const CategoriesWrapper = styled.div`
  margin: 20px -5px 0;
  display: ${({ $listMode }) => ($listMode ? 'block' : 'flex')};
  flex-wrap: ${({ $listMode }) => ($listMode ? 'nowrap' : 'wrap')};

  @media (max-width: 480px) {
    margin: 15px 0 0;
  }
`

const FolderWrapper = styled.div`
  box-sizing: border-box;
  max-width: 210px;
  width: 31%;
  height: 60px;
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid #dfe4f6;
  margin: 5px;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      opacity: 0.3;

      &:hover {
        border-color: #dfe4f6;
      }
    `}

  @media (max-width: 768px) {
  width: 48%;
  max-width: none;
}

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
    margin: 5px 0;
  }
`

const FolderNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 12px;
  color: ${({ theme }) => theme.colors.grey[600]};
`

const FolderName = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`

const HeaderTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
`

const StyledAddButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const AddButtonWrapper = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`

const SectionHeader = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.3px;
  display: flex;
  white-space: nowrap;
  margin-left: 10px;
  ${({ open }) =>
    open &&
    css`
      cursor: pointer;
    `}
`

const BreadcrumbsSign = styled(ChevronRightIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 18px !important;
  margin: 0 5px;
`

const CurrentFolderName = styled(SectionHeader)`
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FilesCounter = styled.span`
  display: inline-block;
  font-weight: bold;
  margin-left: 5px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const CurrentCategoryTitle = styled.div`
  margin: 10px 0 20px;

  @media (max-width: 480px) {
    margin: 10px 10px 15px;
    font-size: 18px;
  }
`

const FolderNameWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const FilesTableScrollWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`

const FilesContainer = styled.div`
  width: 100%;
`

const FilesHeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-sizing: border-box;
  margin-top: 10px;

  @media (max-width: 768px) {
    display: none;
  }
`

const HeaderCell = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const HeaderName = styled(HeaderCell)`
  flex: 2;
  min-width: 150px;
`

const HeaderType = styled(HeaderCell)`
  flex: 1.5;
  min-width: 150px;
  max-width: 315px;
`

const HeaderDate = styled(HeaderCell)`
  flex: 1;
  min-width: 30px;
  max-width: 70px;
`

const HeaderUser = styled(HeaderCell)`
  flex: 1;
  min-width: 30px;
  max-width: 70px;
`

const BulkActionsBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin: 10px 0;

  @media (max-width: 480px) {
    overflow-x: auto;
  }
`
